import { Component, OnDestroy, OnInit, Inject, DOCUMENT } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';

import { SideMenuService } from "../sidemenu.service";
import { SharedDataService } from '../shared.data.service';
import { PermissionService } from '../../../services/permissions/permission.service';
import { expandCollapse } from './sidebar-navigation-animations';

@Component({
    selector: 'app-sidebar-navigation',
    templateUrl: './sidebar-navigation.component.html',
    styleUrls: ['./sidebar-navigation.component.scss'],
    standalone: false,
    animations: [expandCollapse]
})
export class SidebarNavigationComponent implements OnInit, OnDestroy {
  public isSideBarOpen: boolean = false;
  public isSidebarPinned: boolean = false;
  public openSubMenu: boolean = false;
  public sideMenuLinks: any;
  public isSidebarOpen2: any;

  private permissionSub?: Subscription;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private sideMenuService: SideMenuService,
    private sharedDataService: SharedDataService,
    private permissionService: PermissionService,
    private router: Router
  ) { }

  ngOnInit(): void {
    // Rebuilds the menu on every permission change, and once immediately with
    // the current value (permissions$ is a BehaviorSubject).
    //
    // This also fixes a long-standing gap: the previous code only filtered when
    // roles were ALREADY in storage, and the fallback branch had its
    // processSideNavigation() call commented out — so a fresh login rendered an
    // unfiltered sidebar until the next reload.
    this.permissionSub = this.permissionService.permissions$.subscribe(() => {
      this.processSideNavigation(this.sharedDataService.getCurrentUserRoles() || []);
    });

    this.isSidebarPinned = this.document.body.classList.contains('sidebar-pinned');
    this.isSideBarOpen = this.isSidebarPinned;
  }

  ngOnDestroy(): void {
    this.permissionSub?.unsubscribe();
  }

  processSideNavigation(roles: any[] = []) {
    const isSuperAdmin = this.sharedDataService.getIsCurrentUserSuperAdmin();
    const isAdmin = roles.some((role: any) => role?.role_access_type?.slug === 'administrator'); // check if user is admin

    // STEP 1: permission checks based on VIEW-OWN / VIEW-ALL
    const permissions = this.sharedDataService.getPermissions() || [];         
    const canSeeProposal=
      permissions.includes('PROPOSAL-VIEW-OWN') ||
      permissions.includes('PROPOSAL-VIEW-ALL') || isSuperAdmin;
    const canSeeChangeOrders=
      permissions.includes('CHANGE-ORDER-VIEW-OWN') ||
      permissions.includes('CHANGE-ORDER-VIEW-ALL') || isSuperAdmin;
    const canSeeTasks=
      permissions.includes('TASK-VIEW-OWN') ||
      permissions.includes('TASK-VIEW-ALL') || isSuperAdmin;
    const canSeeTickets=
      permissions.includes('TICKET-VIEW-OWN') ||
      permissions.includes('TICKET-VIEW-ALL') || isSuperAdmin;
    const canSeeProducts=
      permissions.includes('PRODUCT-VIEW-OWN') ||
      permissions.includes('PRODUCT-VIEW-ALL') || isSuperAdmin;
    const canSeeProductCategory=
      permissions.includes('PRODUCT-CATEGORY-VIEW-OWN') ||
      permissions.includes('PRODUCT-CATEGORY-VIEW-ALL')|| isSuperAdmin;
    const canSeeServiceCategory=
      permissions.includes('SERVICE-CATEGORY-VIEW-OWN') ||
      permissions.includes('SERVICE-CATEGORY-VIEW-ALL')|| isSuperAdmin;
    const canSeeStaff=
      permissions.includes('STAFF-VIEW-OWN') ||
      permissions.includes('STAFF-VIEW-ALL') || isSuperAdmin;
    const canSeeVendor=
      permissions.includes('VENDOR-VIEW-OWN') ||
      permissions.includes('VENDOR-VIEW-ALL') || isSuperAdmin;
    const canSeeCustomer=
      permissions.includes('CUSTOMER-VIEW-OWN') ||
      permissions.includes('CUSTOMER-VIEW-ALL') || isSuperAdmin;
    const canSeeContact=
      permissions.includes('CONTACT-VIEW-OWN') ||
      permissions.includes('CONTACT-VIEW-ALL') || isSuperAdmin;
    const canSeeBids=
      permissions.includes('BIDS-VIEW-OWN') ||
      permissions.includes('BIDS-VIEW-ALL') || isSuperAdmin;  
    const canSeeWarehouses=
      permissions.includes('WAREHOUSE-VIEW-OWN') ||
      permissions.includes('WAREHOUSE-VIEW-ALL') || isSuperAdmin;
    const canSeeServices=
      permissions.includes('SERVICE-VIEW-OWN') ||
      permissions.includes('SERVICE-VIEW-ALL') || isSuperAdmin;
    const canSeeJob=
      permissions.includes('JOB-VIEW-OWN') ||
      permissions.includes('JOB-VIEW-ALL') || isSuperAdmin;
    const canSeeSubcontractors=
      permissions.includes('SUBCONTRACTOR-VIEW-OWN') ||
      permissions.includes('SUBCONTRACTOR-VIEW-ALL') || isSuperAdmin;
    const canSeeSubcontractorContact=
      permissions.includes('SUBCONTRACTOR-CONTACT-VIEW-OWN') ||
      permissions.includes('SUBCONTRACTOR-CONTACT-VIEW-ALL') || isSuperAdmin;
    const canSeeMilestones=
      permissions.includes('MILESTONE-VIEW-OWN') ||
      permissions.includes('MILESTONE-VIEW-ALL') || isSuperAdmin;
    const canSeeStaffCertificate=
      permissions.includes('STAFF-CERTIFICATE-VIEW-OWN') ||
      permissions.includes('STAFF-CERTIFICATE-VIEW-ALL') || isSuperAdmin;
      const canSeeNotes=
      permissions.includes('NOTE-VIEW-OWN') ||
      permissions.includes('NOTE-VIEW-ALL') || isSuperAdmin;
      const canSeeSubmittals=
      permissions.includes('SUBMITTAL-VIEW-OWN') ||
      permissions.includes('SUBMITTAL-VIEW-ALL') || isSuperAdmin;
    const canSeeTasksTemplates=
      permissions.includes('TASK-TEMPLATES-VIEW-OWN') ||
      permissions.includes('TASK-TEMPLATES-VIEW-ALL') || isSuperAdmin;
    const canSeeJobsites=
      permissions.includes('JOB-SITE-VIEW-OWN') ||
      permissions.includes('JOB-SITE-VIEW-ALL') || isSuperAdmin;
    // Purchase Order's permission set (PURCHASE-ORDER-VIEW-OWN / -VIEW-ALL and
    // the rest) was never seeded into the live `permissions` table (the
    // assigning seeder was skipped), so the normal permission gate hides the
    // module from every non-superadmin user. The PO backend routes are NOT
    // permission-guarded (routes/api.php `purchase/orders` group is
    // Sanctum-only), so this is purely a sidebar-visibility gap and showing
    // it is safe. The frontend only sees the current user's granted
    // permissions, not the global catalog, so it can't tell "unseeded" apart
    // from "not granted to me" — show it unconditionally for now.
    //
    // TODO: once the PURCHASE-ORDER-* permissions are seeded and assigned,
    // restore the gated form:
    //   permissions.includes('PURCHASE-ORDER-VIEW-OWN') ||
    //   permissions.includes('PURCHASE-ORDER-VIEW-ALL') || isSuperAdmin;
    const canSeePurchaseOrder = true;
      const canSeeCalendar=
      permissions.includes('CALENDAR-VIEW-OWN') ||
      permissions.includes('CALENDAR-VIEW-ALL') || isSuperAdmin;
      const canSeeReminder=
      permissions.includes('REMINDER-VIEW-OWN') ||
      permissions.includes('REMINDER-VIEW-ALL') || isSuperAdmin;
      const canSeeHr=
      permissions.includes('STAFF-VIEW-OWN') ||
      permissions.includes('STAFF-VIEW-ALL') ||
      permissions.includes('HR-VIEW-OWN') ||
      permissions.includes('HR-VIEW-ALL') || isSuperAdmin;
      const canSeeDocuments=
      permissions.includes('DOCUMENTS-VIEW-OWN') ||
      permissions.includes('DOCUMENTS-VIEW-ALL') || isSuperAdmin;

 

    // STEP 2: build sidebar
    let menuItems = this.sideMenuService.getMenuItems();

    // non-admin users
    menuItems = menuItems.filter((item: any) => {
      // Proposal logic: hide proposals if user has neither view-own nor view-all
      if (!canSeeProposal) {
        if (item.route === '/proposals') {
          return false;  
        }

        // hide proposals from sublinks if user has no permission
        if (item.sublinks) {  //array under main menu
          item.sublinks = item.sublinks.filter(
            (sublink: any) => sublink.route !== '/proposals'
          );
        }
      }

      // Change Order logic: hide change orders if user has neither view-own nor view-all
      if (!canSeeChangeOrders) {
        if (item.route === '/change-orders') {
          return false;
        }

        // hide change orders from sublinks if user has no permission
        if (item.sublinks) {
          item.sublinks = item.sublinks.filter(
            (sublink: any) => sublink.route !== '/change-orders'
          );
        }
      }
      if (!canSeeTasks) {
        if (item.route === '/tasks') {
          return false;
        }
        if (item.sublinks) {
          item.sublinks = item.sublinks.filter(
            (sublink: any) => sublink.route !== '/tasks'
          );
        }
      }
      if (!canSeeTickets) {
        if (item.route === '/tickets') {
          return false;
        }
        if (item.sublinks) {
          item.sublinks = item.sublinks.filter(
            (sublink: any) => sublink.route !== '/tickets'
          );
        }
      }
      if (!canSeeProducts) {
        if (item.route === '/products') {
          return false;
        }
        if (item.sublinks) {
          item.sublinks = item.sublinks.filter(
            (sublink: any) => sublink.route !== '/products'
          );
        }
      }
      if (!canSeeProductCategory) {
        if (item.route === '/categories/product') {
          return false;
        }
        if (item.sublinks) {
          item.sublinks = item.sublinks.filter(
            (sublink: any) => sublink.route !== '/categories/product'
          );
        }
      }
      if (!canSeeServiceCategory) {
        if (item.route === '/categories/service') {
          return false;
        }
        if (item.sublinks) {
          item.sublinks = item.sublinks.filter(
            (sublink: any) => sublink.route !== '/categories/service'
          );
        }
      }
      if (!canSeeStaff) {
        if (item.route === '/staff') {
          return false;
        }
        if (item.sublinks) {
          item.sublinks = item.sublinks.filter(
            (sublink: any) => sublink.route !== '/staff'
          );
        }
      }
      if (!canSeeVendor) {
        if (item.route === '/vendors') {
          return false;
        }
        if (item.sublinks) {
          item.sublinks = item.sublinks.filter(
            (sublink: any) => sublink.route !== '/vendors'
          );
        }
      }
      if (!canSeeCustomer) {
        if (item.route === '/customers') {
          return false;
        }
        if (item.sublinks) {
          item.sublinks = item.sublinks.filter(
            (sublink: any) => sublink.route !== '/customers'
          );    
        }
      }
      if (!canSeeContact) {
        if (item.route === '/contacts') {
          return false;
        }
        if (item.sublinks) {
          item.sublinks = item.sublinks.filter(
            (sublink: any) => sublink.route !== '/contacts'
          );
        }
      }
      if (!canSeeBids) {
        if (item.route === '/bids') {
          return false;
        }
        if (item.sublinks) {
          item.sublinks = item.sublinks.filter(
            (sublink: any) => sublink.route !== '/bids'
          );
        }
      }
      if (!canSeeWarehouses) {
        if (item.route === '/warehouses') {
          return false;
        }
        if (item.sublinks) {
          item.sublinks = item.sublinks.filter(
            (sublink: any) => sublink.route !== '/warehouses'
          );
        }
      }
      if (!canSeeServices) {
        if (item.route === '/services') {
          return false;
        }
        if (item.sublinks) {
          item.sublinks = item.sublinks.filter(
            (sublink: any) => sublink.route !== '/services'
          );
        }
      }
      if (!canSeeJob) {
        if (item.route === '/jobs') {
          return false;
        }
        if (item.sublinks) {
          item.sublinks = item.sublinks.filter(
            (sublink: any) => sublink.route !== '/jobs'
          );
        }
      }
      if (!canSeeSubcontractors) {
        if (item.route === '/subcontractors') {
          return false;
        }   
        if (item.sublinks) {
          item.sublinks = item.sublinks.filter(
            (sublink: any) => sublink.route !== '/subcontractors'
          );
        }
      }
      if (!canSeeSubcontractorContact) {
        if (item.route === '/subcontractor/contacts') {
          return false;
        }
        if (item.sublinks) {
          item.sublinks = item.sublinks.filter(
            (sublink: any) => sublink.route !== '/subcontractor/contacts'
          );
        }
      }
      if (!canSeeNotes) {
        if (item.route === '/notes') {
          return false;
        }
        if (item.sublinks) {
          item.sublinks = item.sublinks.filter(
            (sublink: any) => sublink.route !== '/notes'
          );
        }
      }
      if (!canSeeMilestones) {
        if (item.route === '/milestones') {
          return false;
        }
        if (item.sublinks) {
          item.sublinks = item.sublinks.filter(
            (sublink: any) => sublink.route !== '/milestones'
          );
        }
      }
      if (!canSeeStaffCertificate) {
        if (item.route === '/staff/certificates') {
          return false;
        }
        if (item.sublinks) {
          item.sublinks = item.sublinks.filter(
            (sublink: any) => sublink.route !== '/staff/certificates'
          );
        }
      }
      if (!canSeeSubmittals) {
        if (item.route === '/submittals') {
          return false;
        }
        if (item.sublinks) {
          item.sublinks = item.sublinks.filter(
            (sublink: any) => sublink.route !== '/submittals'
          );
        }
      }
      if (!canSeeTasksTemplates) {
        if (item.route === '/task/templates') {
          return false;
        }
        if (item.sublinks) {
          item.sublinks = item.sublinks.filter(
            (sublink: any) => sublink.route !== '/task/templates'
          );
        }
      }   
      if (!canSeeJobsites) {
        if (item.route === '/jobsites') {
          return false;
        }
        if (item.sublinks) {
          item.sublinks = item.sublinks.filter(
            (sublink: any) => sublink.route !== '/jobsites'
          );
        }
      }
      if (!canSeePurchaseOrder) {
        if (item.route === '/purchase-order') {
          return false;
        }
        if (item.sublinks) {
          item.sublinks = item.sublinks.filter(
            (sublink: any) => sublink.route !== '/purchase-order'
          );
        }
      }
      if (!canSeeCalendar) {
        if (item.route === '/calendar') {
          return false;
        }
        if (item.sublinks) {
          item.sublinks = item.sublinks.filter(
            (sublink: any) => sublink.route !== '/calendar'
          );
        }
      }
  
      if (!canSeeReminder) {
        if (item.route === '/reminders') {
          return false;
        }
        if (item.sublinks) {
          item.sublinks = item.sublinks.filter(
            (sublink: any) => sublink.route !== '/reminders'
          );
        }
      }
      if (!canSeeHr) {
        if (item.route === '/hr') {
          return false;
        }
        if (item.sublinks) {
          item.sublinks = item.sublinks.filter(
            (sublink: any) => sublink.route !== '/hr'
          );
        }
      }
      if (!canSeeDocuments) {
        if (item.route === '/documents') {
          return false;
        }
        if (item.sublinks) {
          item.sublinks = item.sublinks.filter(
            (sublink: any) => sublink.route !== '/documents'
          );
        }
      }
        return true;
    });
    this.sideMenuLinks = menuItems;
  }

  onMobileToggaleSideBarClose = () => {
    this.document.body.classList.remove('sidebar-open');
  }

  onToggaleSideBar = () => {
    if (this.isSidebarPinned) {
      this.isSidebarPinned = false;
      this.isSideBarOpen = false;
      this.sideMenuService.setSiteMenuToggle(false);
      this.document.body.classList.remove('sidebar-pinned');

      const activeGroupId = this.getActiveGroupId();
      this.sideMenuLinks?.forEach((nav: any) => {
        if (nav.id !== activeGroupId) {
          nav.isSelected = false;
        }
      });
    } else {
      this.expandSidebar();
    }
  }

  private expandSidebar(): void {
    this.isSidebarPinned = true;
    this.isSideBarOpen = true;
    this.sideMenuService.setSiteMenuToggle(true);
    this.document.body.classList.add('sidebar-pinned');
  }


  onMobileToggaleSideBarOpen = () => {
    this.document.body.classList.add('sidebar-open');
  }

  isOpen(id: any) {
    const wasCollapsed = !this.isSideBarOpen;
    if (wasCollapsed) {
      this.expandSidebar();
    }

    const activeGroupId = this.getActiveGroupId();
    this.sideMenuLinks.forEach((nav: any) => {
      if (nav.id == id) {
        nav.isSelected = wasCollapsed ? true : !nav.isSelected;
      } else if (nav.id !== activeGroupId) {
        nav.isSelected = false;
      }
    });
  }

  private getActiveGroupId(): any {
    const currentUrl = this.router.url;
    const activeGroup = this.sideMenuLinks?.find((nav: any) =>
      nav.sublinks?.some((sub: any) => sub.route && currentUrl.startsWith(sub.route))
    );
    return activeGroup?.id;
  }

}
